/**
 * PROGRAMAS INTERNOS DA SERCAMP — o conteúdo dos dois materiais, em um lugar só.
 *
 * São DOIS programas diferentes, e é assim que eles aparecem no sistema: cada
 * um tem o seu formulário, o seu link e a sua lista de respostas.
 *
 *   • REALIZE UM SONHO      — duas edições por ano; a pessoa conta o sonho e
 *                             uma comissão escolhe um por edição.
 *   • IDEIAS QUE TRANSFORMAM — fluxo contínuo; a pessoa manda uma ideia de
 *                             melhoria e pode ganhar um prêmio.
 *
 * Este arquivo é a FONTE do texto: a página pública que o colaborador abre e a
 * tela do RH leem daqui. Sem isso, o mesmo texto (as regras, os critérios, as
 * categorias, os prêmios) acabaria copiado em dois lugares e um deles ficaria
 * desatualizado na primeira mudança de campanha.
 *
 * O texto foi transcrito dos materiais de divulgação aprovados. Alterou o
 * cartaz? Altere aqui — as duas telas seguem juntas.
 */

export type ProgramaKey = "sonho" | "ideia";

/* ------------------------------------------------------------------ *
 *  Campos que TODA resposta carrega                                   *
 * ------------------------------------------------------------------ *
 * O NOME identifica quem respondeu — é ele que liga a resposta à pessoa
 * quando ela volta para o Endomarketing, e é o que a ficha impressa
 * pede. O nome é comparado sem acento e sem caixa, então "JOSÉ DA
 * SILVA" e "Jose da Silva" são a mesma pessoa.
 *
 * ⚠️ Homônimos: duas pessoas com o mesmo nome ficam indistinguíveis
 * para o sistema (uma bloqueia a inscrição da outra na mesma edição, e
 * o cadastro casado pode ser o da pessoa errada). Se isso aparecer na
 * prática, o caminho é voltar a pedir um documento no formulário.      */

export interface CampoPrograma {
  /** Nome da coluna no banco (e a chave no JSON enviado). */
  nome: string;
  label: string;
  tipo: "text" | "textarea" | "currency" | "tel" | "select";
  obrigatorio?: boolean;
  /** Texto de apoio embaixo do campo. */
  dica?: string;
  /** Ocupa a linha inteira do formulário. */
  largo?: boolean;
  opcoes?: readonly string[];
}

/* ------------------------------------------------------------------ *
 *  1) REALIZE UM SONHO                                                *
 * ------------------------------------------------------------------ */

export const SONHO = {
  key: "sonho" as const,
  nome: "Realize um Sonho",
  chamada: "A SERCAMP acredita em pessoas. E todo mundo carrega um sonho.",
  descricao:
    "Duas vezes por ano, vamos escolher um colaborador para realizar um pequeno sonho pessoal " +
    "ou familiar, com apoio da Sercamp. Porque cuidar de quem constrói nossa história faz parte " +
    "de quem somos.",
  frase: "Grandes histórias também começam com pequenos sonhos.",
  citacao:
    "Nosso maior patrimônio são as pessoas. E quando um colaborador realiza um sonho, todos nós " +
    "crescemos juntos. Essa é a cultura Sercamp.",

  destaques: [
    {
      titulo: "2 edições por ano",
      texto: "Uma oportunidade para transformar um sonho em realidade.",
    },
    {
      titulo: "Até R$ 2.000",
      texto: "Valor máximo por edição definido previamente pela empresa.",
    },
    {
      titulo: "Sonhos relevantes",
      texto: "Sonhos que geram impacto positivo na vida do colaborador ou família.",
    },
    { titulo: "Para todos", texto: "Igualdade, respeito e transparência em todo o processo." },
  ],

  comoFunciona: [
    {
      titulo: "Inscrição",
      texto: "O colaborador preenche o formulário contando qual é o seu sonho e por que ele é importante.",
    },
    { titulo: "Avaliação", texto: "Uma comissão analisa os sonhos com base nos critérios do programa." },
    { titulo: "Escolha", texto: "Um sonho será escolhido para ser realizado em cada edição." },
    { titulo: "Realização", texto: "A Sercamp realiza o sonho e compartilha essa conquista com todos." },
  ],

  regras: [
    "Podem participar todos os colaboradores efetivos.",
    "Cada colaborador pode participar 1 vez por edição.",
    "O sonho deve ser pessoal ou familiar.",
    "O valor solicitado deve estar dentro do limite definido.",
    "O sonho deve ser íntegro, lícito e viável de ser realizado.",
    "A decisão da comissão é soberana.",
    "A empresa não converte o valor em dinheiro.",
  ],

  criterios: [
    "Impacto positivo na vida do colaborador ou família",
    "Viabilidade e adequação ao orçamento",
    "História inspiradora e alinhada aos valores da Sercamp",
    "Conduta e engajamento com a cultura da empresa",
  ],

  exemplos: [
    "Levar meus filhos para um passeio especial",
    "Fazer um curso profissionalizante ou técnico",
    "Comprar uma bicicleta para meu filho",
    "Fazer uma viagem curta com minha família",
    "Reformar um ambiente da minha casa",
    "Realizar um jantar especial com meus pais",
    "Comprar um instrumento musical",
    "Comprar um equipamento para estudar",
  ],

  edicoes: ["1ª edição: junho", "2ª edição: dezembro"],

  /** A ficha de inscrição, na ordem em que ela aparece no material. */
  campos: [
    { nome: "nome", label: "Nome completo", tipo: "text", obrigatorio: true, largo: true },
    { nome: "telefone", label: "Telefone", tipo: "tel" },
    { nome: "setor", label: "Setor", tipo: "text" },
    { nome: "funcao", label: "Função", tipo: "text" },
    { nome: "tempo_empresa", label: "Tempo de empresa", tipo: "text" },
    {
      nome: "sonho",
      label: "Qual é o seu sonho?",
      tipo: "textarea",
      obrigatorio: true,
      dica: "Conte com detalhes.",
      largo: true,
    },
    {
      nome: "motivo",
      label: "Por que esse sonho é importante para você?",
      tipo: "textarea",
      obrigatorio: true,
      largo: true,
    },
    {
      nome: "valor",
      label: "Qual o valor aproximado para realizar esse sonho?",
      tipo: "currency",
      dica: "Em reais. O limite da edição é definido pela empresa.",
    },
  ] satisfies CampoPrograma[],

  aceite: "Declaro que li e concordo com as regras do programa.",

  /** Situações que o RH dá a uma inscrição. A 1ª é a que nasce com ela. */
  situacoes: ["Recebida", "Em análise", "Selecionada", "Não selecionada"] as const,
} as const;

/* ------------------------------------------------------------------ *
 *  2) IDEIAS QUE TRANSFORMAM                                          *
 * ------------------------------------------------------------------ */

export const IDEIA = {
  key: "ideia" as const,
  nome: "Ideias que Transformam",
  chamada: "Sua ideia pode melhorar a Sercamp.",
  descricao:
    "Acreditamos que as melhores ideias nascem de quem vive a realidade do dia a dia. " +
    "Por isso, queremos ouvir você!",
  frase: "Pense • Participe • Transforme",
  citacao:
    "Não importa o seu cargo. Uma grande transformação pode começar com uma ideia simples.",

  comoFunciona: [
    { titulo: "Preencha", texto: "Preencha o formulário disponível nos canais internos." },
    { titulo: "Envie", texto: "Envie sua ideia." },
    { titulo: "Retorno", texto: "Nossa equipe avaliará e você receberá retorno do resultado." },
  ],

  /** As 12 frentes do material — viram o campo "categoria" do formulário. */
  categorias: [
    "Redução de desperdícios",
    "Economia de materiais e recursos",
    "Segurança no trabalho",
    "Melhoria de processos",
    "Organização dos setores",
    "Aumento da produtividade",
    "Qualidade dos serviços",
    "Atendimento aos clientes",
    "Bem-estar dos colaboradores",
    "Sustentabilidade",
    "Novas formas de executar atividades",
    "Melhorias em equipamentos e ferramentas",
  ],

  /** Os quatro níveis de premiação, na ordem do material. */
  premios: [
    { titulo: "Ideia Destaque", texto: "Certificado + reconhecimento interno." },
    { titulo: "Ideia de Impacto", texto: "Vale-presente de até R$ 150." },
    { titulo: "Ideia Transformadora", texto: "Premiação de até R$ 300." },
    {
      titulo: "Ideia Extraordinária",
      texto: "Premiação especial definida pela Diretoria, de acordo com o impacto gerado.",
    },
  ],

  campos: [
    { nome: "nome", label: "Nome completo", tipo: "text", obrigatorio: true, largo: true },
    { nome: "telefone", label: "Telefone", tipo: "tel" },
    { nome: "setor", label: "Setor", tipo: "text" },
    { nome: "funcao", label: "Função", tipo: "text" },
    {
      nome: "categoria",
      label: "Tipo de ideia",
      tipo: "select",
      obrigatorio: true,
      largo: true,
      opcoes: [
        "Redução de desperdícios",
        "Economia de materiais e recursos",
        "Segurança no trabalho",
        "Melhoria de processos",
        "Organização dos setores",
        "Aumento da produtividade",
        "Qualidade dos serviços",
        "Atendimento aos clientes",
        "Bem-estar dos colaboradores",
        "Sustentabilidade",
        "Novas formas de executar atividades",
        "Melhorias em equipamentos e ferramentas",
      ],
    },
    {
      nome: "titulo",
      label: "Título da ideia",
      tipo: "text",
      obrigatorio: true,
      dica: "Uma frase que resuma a ideia.",
      largo: true,
    },
    {
      nome: "descricao",
      label: "Descreva a sua ideia",
      tipo: "textarea",
      obrigatorio: true,
      dica: "O que muda, onde e como funcionaria no dia a dia.",
      largo: true,
    },
    {
      nome: "beneficio",
      label: "Que ganho essa ideia traz?",
      tipo: "textarea",
      dica: "Economia, segurança, tempo, qualidade… o que você enxerga.",
      largo: true,
    },
  ] satisfies CampoPrograma[],

  situacoes: ["Recebida", "Em análise", "Premiada", "Não selecionada"] as const,

  /** Preenchido pelo RH quando a ideia é premiada. */
  premiacoes: [
    "Ideia Destaque",
    "Ideia de Impacto",
    "Ideia Transformadora",
    "Ideia Extraordinária",
  ] as const,
} as const;

/* ------------------------------------------------------------------ *
 *  Comum aos dois                                                     *
 * ------------------------------------------------------------------ */

/** Os valores que fecham os dois materiais. */
export const VALORES = ["Transparência", "Humildade", "Respeito", "Excelência"];

/** Assinatura do rodapé do material do Sonho. */
export const ASSINATURA = "Juntos, construímos mais que obras. Construímos histórias.";

/** Para onde a ficha em papel vai — aparece na página pública. */
export const EMAIL_RH = "rh@sercamp.com.br";

export const PROGRAMAS = { sonho: SONHO, ideia: IDEIA } as const;

/**
 * O cartaz oficial do programa, servido pelo próprio sistema
 * (server/programa-banner.ts). É o banner da página do link e da aba do RH, e
 * a versão "-og" é a que aparece na prévia do WhatsApp.
 */
export function bannerDoPrograma(key: string): string {
  return `/programa-banner/${key === "ideia" ? "ideia" : "sonho"}.jpg`;
}

/** O programa pela chave, com o texto pronto para a tela. */
export function programaPorKey(key: string) {
  return key === "ideia" ? IDEIA : SONHO;
}

/** "Realize um Sonho" / "Ideias que Transformam". */
export function nomePrograma(key: string): string {
  return programaPorKey(key).nome;
}

/* ------------------------------------------------------------------ *
 *  Nome — identifica quem respondeu                                   *
 * ------------------------------------------------------------------ */

/**
 * Nome comparável: sem acento, sem caixa e sem espaço dobrado.
 *
 * É assim que a resposta acha a pessoa no cadastro e que a regra de "uma
 * inscrição por edição" é aplicada. Mesma normalização que o RH já usa para
 * casar colaborador por nome em outras telas.
 */
export function normNome(v: unknown): string {
  return String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}
